import axios from "axios";
import userManagementApi from "./userManagementApi";

const buildError = (error) => {
  if (axios.isAxiosError(error)) {
    const err = new Error(
      error?.response?.data?.message ||
      error?.response?.data ||
      error.message ||
      "User management request failed"
    );

    err.status = error.response?.status;
    err.data = error.response?.data;

    return err;
  }

  return error;
};

export const registerPortalUser = async ({
  fullName,
  mobile,
  password,
  email = "",
  role = "patient",
  department = "",
  organizationId = null,
}) => {
  try {
    const payload = {
      fullName,
      mobile,
      password,
      role,
      email,
      department,
    };

    if (organizationId !== null && organizationId !== undefined) {
      payload.organizationId = organizationId;
    }

    const response = await userManagementApi.post(
      "/register",
      payload
    );

    return response.data;
  } catch (error) {
    throw buildError(error);
  }
};

export const updatePortalUser = async (payload) => {
  try {
    const response = await userManagementApi.post(
      "/updateUser",
      payload
    );

    return response.data;
  } catch (error) {
    throw buildError(error);
  }
};

/**
 * Enable or disable portal login for a user (used when activating/deactivating doctors).
 * Tries dedicated endpoint first, then updateUser with is_active flag.
 */
export const setPortalUserLoginEnabled = async (userId, enabled) => {
  if (userId == null || userId === "") {
    throw new Error("User ID is required to update login status");
  }

  const payload = {
    id: userId,
    is_active: enabled ? 1 : 0,
    active: enabled,
  };

  try {
    const response = await userManagementApi.post(
      "/setUserStatus",
      payload,
      {
        validateStatus: (status) => status < 600,
      }
    );

    if (response.status < 400) {
      return response.data;
    }
  } catch (error) {
    if (error.response?.status !== 404) {
      console.warn(
        "[setPortalUserLoginEnabled] setUserStatus unavailable:",
        error.message
      );
    }
  }

  try {
    const response = await userManagementApi.post(
      "/updateUser",
      payload
    );

    return response.data;
  } catch (error) {
    throw buildError(error);
  }
};

/**
 * Validate if a user (by mobile) belongs to the specified organization
 * This is called BEFORE login to ensure user belongs to selected organization
 * @param {string} mobile - Mobile number
 * @param {number} organizationId - Organization ID to validate against
 * @returns {Promise<{valid: boolean, user?: object}>} Validation result
 */
export const validateUserOrganization = async (mobile, organizationId) => {
  if (!mobile || !organizationId) {
    throw new Error("Mobile number and organizationId are required");
  }
  
  try {
    console.log(`[userManagementService] Validating user ${mobile} for organization ${organizationId}`);
    const response = await userManagementApi.get(
      "/validateUserOrganization",
      {
        params: {
          mobile: mobile.trim(),
          organizationId: Number(organizationId)
        }
      }
    );
    
    const data = response.data;
    console.log(`[userManagementService] ✅ User validation result:`, data);
    
    // Backend should return { valid: true/false, user: {...} }
    // If valid is true, user belongs to the organization
    if (data.valid === true) {
      return { valid: true, user: data.user || null };
    } else {
      // User doesn't belong to the organization
      const error = new Error(data.message || "User does not belong to the selected hospital organization");
      error.status = 403;
      error.response = { status: 403, data: { message: data.message || "User does not belong to the selected hospital organization" } };
      throw error;
    }
  } catch (error) {
    console.error(`[userManagementService] Error validating user organization:`, error);
    // If it's already a built error, re-throw it
    if (error.status) {
      throw error;
    }
    // Otherwise, build the error
    throw buildError(error);
  }
};

const parseExistsFlag = (data) => {
  if (data == null) return false;
  if (typeof data === 'boolean') return data;
  if (typeof data === 'object') {
    return data.exists === true || data.exists === 'true';
  }
  return false;
};

/**
 * GET /check-email or /check-mobile — some backends return HTTP 500 with { exists: false }.
 * Treat any parseable body as authoritative; do not surface as a failed request in the console.
 */
const runUserExistsCheck = async (endpoint, params) => {
  try {
    const response = await userManagementApi.get(
      `/${endpoint}`,
      {
        params,
        validateStatus: (status) => status < 600,
      }
    );

    return parseExistsFlag(response.data);
  } catch (error) {
    if (error.response?.data != null) {
      return parseExistsFlag(error.response.data);
    }

    return false;
  }
};

/**
 * Check if admin email already exists across all users
 * Uses user management service endpoint: GET /check-email?email=...
 * @param {string} email - Email to check
 * @param {number|string|null} excludeUserId - Optional user id to exclude (edit flows)
 * @returns {Promise<boolean>} True if email exists, false otherwise
 */
export const checkAdminEmailExists = async (email, excludeUserId = null) => {
  if (!email || !String(email).trim()) {
    return false;
  }

  const params = {
    email: String(email).trim().toLowerCase(),
    ...(excludeUserId != null ? { excludeUserId } : {}),
  };

  return runUserExistsCheck('check-email', params);
};

/**
 * Check if admin mobile already exists across all users
 * Uses user management service endpoint: GET /check-mobile?mobile=...
 * @param {string} mobile - Mobile number to check
 * @param {number|string|null} excludeUserId - Optional user id to exclude (edit flows)
 * @returns {Promise<boolean>} True if mobile exists, false otherwise
 */
export const checkAdminMobileExists = async (mobile, excludeUserId = null) => {
  if (!mobile || !String(mobile).trim()) {
    return false;
  }

  const params = {
    mobile: String(mobile).trim().replace(/\D/g, ''),
    ...(excludeUserId != null ? { excludeUserId } : {}),
  };

  return runUserExistsCheck('check-mobile', params);
};

export default {
  registerPortalUser,
  updatePortalUser,
  setPortalUserLoginEnabled,
  validateUserOrganization,
  checkAdminEmailExists,
  checkAdminMobileExists,
};

